import type { BusPayload } from "../core/messages";

/**
 * Built-in message transforms for <gr-link> — the Max/MSP glue objects
 * (`scale`, `slide`, comparator, speedlim) as declarative attributes:
 *
 *   <gr-link from="pot/raw" to="synth/pitch" scale="0 4095 100 1600" clamp></gr-link>
 *   <gr-link from="pot/raw" to="ui/level"    smooth="0.2"></gr-link>
 *   <gr-link from="ldr/lux" to="lamp/on"     threshold="500" hysteresis="20"></gr-link>
 *   <gr-link from="imu/x"   to="chart/x"     throttle="100"></gr-link>
 *
 * Stages run in a fixed order: scale → smooth → threshold → throttle.
 * Numeric stages pass non-numeric payloads through untouched; throttle
 * applies to everything. Returning undefined drops the message.
 */

export interface ScaleSpec {
  inMin: number;
  inMax: number;
  outMin: number;
  outMax: number;
}

/** Parse `"inMin inMax outMin outMax"` (spaces or commas). Null when malformed. */
export function parseScale(raw: string | null): ScaleSpec | null {
  if (!raw) return null;
  const parts = raw.split(/[\s,]+/).filter(Boolean).map(Number);
  if (parts.length !== 4 || parts.some(Number.isNaN)) return null;
  const [inMin, inMax, outMin, outMax] = parts;
  if (inMin === inMax) return null;
  return { inMin, inMax, outMin, outMax };
}

export interface TransformSpec {
  scale?: ScaleSpec;
  /** Clamp the scaled value to the output range. */
  clamp?: boolean;
  /** EMA factor in (0, 1] — smaller is smoother. */
  smooth?: number;
  /** Emit booleans instead of numbers; only on state change. */
  threshold?: number;
  /** Schmitt band below `threshold` before flipping back to false. */
  hysteresis?: number;
  /** Minimum ms between forwarded messages (leading edge wins). */
  throttle?: number;
}

export type Transform = (payload: BusPayload) => BusPayload | undefined;

/**
 * Build a stateful pipeline from a spec. State (EMA accumulator, Schmitt
 * state, throttle clock) lives in the returned closure — create one
 * pipeline per link, not per message.
 */
export function createPipeline(spec: TransformSpec): Transform {
  const stages: Transform[] = [];

  if (spec.scale) {
    const { inMin, inMax, outMin, outMax } = spec.scale;
    const clamp = spec.clamp === true;
    stages.push((payload) => {
      if (typeof payload !== "number") return payload;
      let v = outMin + ((payload - inMin) / (inMax - inMin)) * (outMax - outMin);
      if (clamp) {
        const lo = Math.min(outMin, outMax);
        const hi = Math.max(outMin, outMax);
        v = Math.min(hi, Math.max(lo, v));
      }
      return v;
    });
  }

  if (typeof spec.smooth === "number" && spec.smooth > 0 && spec.smooth <= 1) {
    const alpha = spec.smooth;
    let ema: number | null = null;
    stages.push((payload) => {
      if (typeof payload !== "number") return payload;
      ema = ema === null ? payload : ema + alpha * (payload - ema);
      return ema;
    });
  }

  if (typeof spec.threshold === "number" && !Number.isNaN(spec.threshold)) {
    const threshold = spec.threshold;
    const hysteresis = Math.max(0, spec.hysteresis ?? 0);
    let state: boolean | null = null;
    stages.push((payload) => {
      if (typeof payload !== "number") return payload;
      const next = state === true ? payload >= threshold - hysteresis : payload >= threshold;
      if (next === state) return undefined;
      state = next;
      return next;
    });
  }

  if (typeof spec.throttle === "number" && spec.throttle > 0) {
    const minGap = spec.throttle;
    let lastAt = -Infinity;
    stages.push((payload) => {
      const now = Date.now();
      if (now - lastAt < minGap) return undefined;
      lastAt = now;
      return payload;
    });
  }

  return (payload) => {
    let value: BusPayload | undefined = payload;
    for (const stage of stages) {
      if (value === undefined) return undefined;
      value = stage(value);
    }
    return value;
  };
}
